<style lang="stylus">
    .pagebox
        position absolute
        left 0
        top 0
        width 0
        height 0
        .shapebox
            width 100%
            height 100%
            pointer-events auto
            transform-origin 0 0
            &.outScreen
                position absolute
                left -10000px
</style>

<template>
    <div
        class="pagebox"
        v-show="show"
        :style="{
            width: `${chartInfo.position.width}px`,
            height: `${chartInfo.position.height}px`,
            transform: chartInfo.position.pageMt,
        }"
    >
        <div
            class="shapebox"
            ref="main"
            :style="{
                transform: chartInfo.position.shapetMt,
            }"
            @click="clickShapebox"
            @mousemove="moveOnShapebox"
            @dblclick.stop="showEditor"
            @mousedown="handleMousedown"
            @mouseleave="handleLeave"
        ></div>
        <div
            class="shapebox outScreen"
            ref="svgMain"
            :style="{
                transform: chartInfo.position.shapetMt,
            }"
        ></div>
    </div>
</template>

<script lang="ts">
    import { Component, Prop, Vue, Watch } from "vue-property-decorator";
    import * as echarts from "echarts";
    import { debounce } from "lodash";
    import { fixSetOption, formatEchartOptions } from "@/app/components/LeftPanel/ChartData";
    import { chartData } from "@/app/components/FloatTool/echart/chartData";
    declare var EchartTheme: any;

    @Component
    export default class EchartBox extends Vue {
        @Prop()
        offset;
        @Prop()
        chartInfo;

        chart = null;
        svgChart = null;
        show = true;
        clickTimer = 0;

        get firstAnimation() {
            return this.$store.state.newEchart.firstAnimation;
        }
        set firstAnimation(v) {
            this.$store.commit("setNewEChartState", {
                key: "firstAnimation",
                value: v,
            });
        }
        initSvg(option: any, themeName = undefined) {
            let svgMain = this.$refs.svgMain as HTMLElement;
            this.svgChart = echarts.init(svgMain, themeName, { renderer: "svg" });
            this.svgChart.setOption(fixSetOption(option));
        }
        initEvent() {
            this.chart.on("mousedown", this.handleChartdown);
            this.chart.on("mousemove", this.handleChartmove);
            this.chart.on("mouseup", this.handleChartup);
            this.chart.on("click", this.handleChartclick);
            this.chart.on("legendselectchanged", this.handleChartlegend);
        }
        initChart(option) {
            this.$nextTick(() => {
                let main = this.$refs.main as HTMLElement;
                this.edSunburstRootNodeId = "";
                this.chart && echarts.dispose(this.chart);
                this.chart = echarts.init(main, undefined, { renderer: "canvas", devicePixelRatio: 4 });
                this.chart.on("finished", this.finishRender);
                let originOption = JSON.parse(option);
                this.initSvg(fixSetOption(originOption));
                if (!this.firstAnimation) {
                    this.chart.setOption(
                        fixSetOption(
                            Object.assign(originOption, {
                                animation: false,
                            })
                        )
                    );
                    setTimeout(() => {
                        originOption.animation = true;
                        this.chart.setOption(fixSetOption(originOption));
                        this.setSunburstRootToNode(originOption);
                    }, 0);
                } else {
                    this.chart.setOption(
                        fixSetOption(
                            Object.assign(originOption, {
                                animation: true,
                            })
                        )
                    );
                    this.firstAnimation = false;
                    this.setSunburstRootToNode(originOption);
                }
                this.initEvent();

                const bgColor = originOption.backgroundColor ?? "";
                this.$store.commit("setEChartState", {
                    type: "activeBgColor",
                    data: bgColor,
                });
            });
        }
        removeTextColor(option) {
            delete option?.textStyle?.color;
            delete option.label?.color;
            option.legend?.forEach((element) => {
                delete element.textStyle?.color;
            });
            if (option.xAxis) {
                option.xAxis.forEach((element) => {
                    delete element.axisLabel?.color;
                    delete element.axisLine?.lineStyle?.color;
                    delete element.nameTextStyle?.color;
                });
            }
            if (option.yAxis) {
                option.yAxis.forEach((element) => {
                    delete element.axisLabel?.color;
                    delete element.axisLine?.lineStyle?.color;
                    delete element.nameTextStyle?.color;
                });
            }
            if (option.radiusAxis) {
                option.radiusAxis.forEach((element) => {
                    delete element.axisLabel?.color;
                    delete element.axisLine?.lineStyle?.color;
                    delete element.nameTextStyle?.color;
                });
            }
        }
        useTheme(themeName) {
            if (this.activeChartId != this.chartInfo.itemId) return;
            let option = JSON.parse(JSON.stringify(this.chart.getOption()));

            this.cleanThemeOption(themeName, option);
            this.setThemeColor(themeName, option);
            this.removeTextColor(option);

            option.edTheme = themeName;

            let main = this.$refs.main as HTMLElement;
            this.chart && echarts.dispose(this.chart);
            this.chart = echarts.init(main, themeName, { renderer: "canvas", devicePixelRatio: 4 });
            this.chart.on("finished", this.finishRender);

            const chartType = this.activeChartType;
            const chartId = this.chartInfo.itemId;
            const onFinish = () => {
                this.updateStoreOption({ chartType, chartId });
                this.chart.off("finished", onFinish);
                const nowOption = this.chart.getOption();
                const bgColor = nowOption.backgroundColor ?? "";
                this.$store.commit("setEChartState", {
                    type: "activeBgColor",
                    data: bgColor,
                });
            };

            this.chart.on("finished", onFinish);
            this.initSvg(fixSetOption(option), themeName);
            this.chart.setOption(fixSetOption(option));
            this.initEvent();
        }
        cleanThemeOption(themeName, option) {
            let theme = EchartTheme.getEchartTheme(themeName);
            const cleanObj = (obj1, obj2) => {
                if (Object.prototype.toString.call(obj1) == "[object Object]") {
                    let keys1 = Object.keys(obj1);
                    for (let item of keys1) {
                        if (obj1[item] && obj2[item]) {
                            if (Object.prototype.toString.call(obj2[item]) == "[object Object]") {
                                cleanObj(obj1[item], obj2[item]);
                            } else {
                                delete obj1[item];
                            }
                        }
                    }
                } else if (Object.prototype.toString.call(obj1) == "[object Array]") {
                    obj1.forEach((element) => {
                        cleanObj(element, obj2);
                    });
                }
            };
            cleanObj(option, theme);
        }
        setThemeColor(themeName, option) {
            // 旭日图设置规则？
            let colorList = EchartTheme.getEchartThemeColor(themeName);
            let theme = EchartTheme.getEchartTheme(themeName);
            if (colorList) {
                const getColor = (index) => {
                    return chartData.getSeriesColor.call(
                        {
                            option: {
                                color: colorList,
                            },
                        },
                        index
                    );
                };
                if (["pie", "radar", "sankey", "funnel"].includes(this.activeChartType)) {
                    option.series[0].data.forEach((element, index) => {
                        if (!["none", "transparent"].includes(element.itemStyle.color)) {
                            element.itemStyle.color = getColor(index);
                        }
                    });

                    if (option.edEchartLabel === "pie06") {
                        option.series[1].data.forEach((element, index) => {
                            if (!["none", "transparent"].includes(element.itemStyle.color)) {
                                element.itemStyle.color = getColor(index);
                            }
                        });
                    }

                    if (this.activeChartType == "sankey") {
                        option.series.forEach((element) => {
                            if (element.lineStyle.color !== "source") {
                                element.lineStyle.color = ["theme9", "theme10"].includes(themeName) ? "#fff" : "#314656";
                            }
                            element.links.forEach((link) => {
                                link.lineStyle = { opacity: 0.2 };
                            });
                        });
                    }
                } else if (this.activeChartType == "sunburst") {
                    const setChildColor = (item, color) => {
                        let child = item.children;
                        if (child && child.length > 0) {
                            child.forEach((element) => {
                                setChildColor(element, color);
                            });
                        }
                        if (item.itemStyle) {
                            Object.assign(item.itemStyle, {
                                color,
                            });
                        } else {
                            item.itemStyle = {
                                color,
                            };
                        }
                    };
                    option.series[0].data.forEach((element, index) => {
                        let color = getColor(index + 1);
                        setChildColor(element, color);
                    });
                } else if (this.activeChartType == "heatmap") {
                    option.visualMap[0].controller.inRange.color = theme.visualMap.color;
                    option.visualMap[0].target.inRange.color = theme.visualMap.color;
                } else if (this.activeChartType == "candlestick") {
                    let color1 = getColor(0);
                    let color2 = getColor(1);
                    option.series.forEach((element) => {
                        element.itemStyle.color = color1;
                        element.itemStyle.color0 = color2;
                        element.itemStyle.borderColor = color1;
                        element.itemStyle.borderColor0 = color2;
                    });
                } else if (this.activeChartType == "parallel") {
                    option.series.forEach((element, index) => {
                        element.lineStyle.color = getColor(index);
                    });
                } else if (this.activeChartType == "graph") {
                    option.series[0].categories.forEach((element, index) => {
                        element.itemStyle.color = getColor(index);
                    });
                } else if (this.activeChartType == "scatter" && option.visualMap && option.visualMap.length > 0) {
                    option.visualMap[0].controller.inRange.color = theme.visualMap.color;
                    option.visualMap[0].target.inRange.color = theme.visualMap.color;
                } else if (this.activeChartType == "waterfall") {
                    option.series[1].itemStyle.color = getColor(0);
                    option.series[2].itemStyle.color = getColor(1);
                    for (let i = 0; i < option.series[1].data.length; i++) {
                        delete option.series[1].data[i]?.itemStyle;
                        delete option.series[2].data[i]?.itemStyle;
                    }
                } else if (this.activeChartType == "boxplot") {
                    const color = getColor(0);
                    option.edEchartMap.forEach((item) => {
                        item.color = color;
                    });
                } else {
                    option.series.forEach((element, index) => {
                        if (!["none", "transparent"].includes(element.itemStyle.color)) {
                            element.itemStyle.color = getColor(index);
                        }
                    });
                }
            }

            // 饼图会莫名出现 borderWidth = 1，手动置为 0
            if (this.activeChartType === "pie") {
                option.series.forEach((serie) => {
                    if (serie.itemStyle) {
                        serie.itemStyle.borderWidth = 0;
                    }
                });
            }
        }
        chartDown = false;
        chartMove = false;
        handleChartdown() {
            if (this.activeChartType == "sankey") {
                this.chartDown = true;
            }
        }
        handleChartmove() {
            if (this.chartDown) {
                this.chartMove = true;
            }
        }
        handleChartup() {
            if (this.chartMove) {
                this.chartMove = false;
                this.generatePreview();
            }
            this.chartDown = false;
        }

        // 旭日图下钻的根节点ID
        edSunburstRootNodeId = "";
        setSunburstRootToNode(optionObj) {
            if (this.edSunburstRootNodeId === optionObj.edSunburstRootNodeId) {
                return;
            }
            this.edSunburstRootNodeId = optionObj.edSunburstRootNodeId ?? "";
            if (this.edSunburstRootNodeId) {
                this.chart.dispatchAction({
                    type: "sunburstRootToNode",
                    targetNode: this.edSunburstRootNodeId,
                    animation: false,
                });
            }
        }
        handleChartclick(params) {
            // 旭日图点击
            if (params.componentSubType === "sunburst") {
                const option = this.chart.getOption();
                const data = option.series[0].data;
                option.edSunburstRootNodeId = params.data.id;
                if (params.data.id) {
                    // 点击了数据项，进行下钻
                    this.edSunburstRootNodeId = params.data.id;
                } else {
                    // 点击了上钻区域，进行上钻
                    this.edSunburstRootNodeId = this.findParentId(JSON.parse(JSON.stringify(data)), this.edSunburstRootNodeId);
                }
                const echartItem = window["_view"]?._activePage?.getItemByID(this.chartInfo.itemId);
                if (echartItem) {
                    echartItem.setOptions(JSON.stringify({ ...option, edSunburstRootNodeId: this.edSunburstRootNodeId }));
                    echartItem.generateEChartImage();
                }
            }
        }
        findParentId(list, id) {
            const stack = list;
            while (stack.length) {
                const node = stack.pop();
                if ((node?.children || []).some((item) => item.id === id)) {
                    return node?.id ?? "";
                }
                if (node.children && node.children.length) {
                    stack.push(...node.children.reverse());
                }
            }
            return "";
        }

        handleChartlegend() {
            this.generatePreview();
        }
        generatePreview() {
            const chartType = this.chartInfo._chartType;
            const chartId = this.chartInfo.itemId;
            let obj = {
                chartType,
                chartId,
            };
            const index = this.taskList.findIndex((item) => item.chartId == chartId);
            if (index == -1) {
                this.taskList.push(obj);
            }
        }
        taskList = [];
        finishRender() {
            while (this.taskList.length > 0) {
                let obj = this.taskList.shift();
                const { chartType, chartId } = obj;
                this.updateStoreOption({ chartType, chartId });
            }
        }
        clickShapebox() {
            if (this.clickTimer) return;
            this.clickTimer = setTimeout(() => {
                window["_view"].selectEchartBox(this.chartInfo.itemId);
            }, 300);
        }
        handleMousedown() {
            this.setClickFlag(1);
            const mouseup = () => {
                this.setClickFlag(0);
                document.removeEventListener("mouseup", mouseup);
            };
            document.addEventListener("mouseup", mouseup);
        }
        setClickFlag(flag) {
            this.$store.commit("setStatus", {
                type: "echartClickBlock",
                data: flag,
            });
        }
        moveOnShapebox(e) {
            let pageX = e.pageX,
                pageY = e.pageY;
            this.getSvgDomByPosition(pageX, pageY);
            window["_view"].checkEchartInner(pageX - this.offset[0], pageY - this.offset[1]);
        }

        getSvgDomByPosition(x: number, y: number) {
            let svg = document.getElementById("opesvg");
            const checkInside = (item: HTMLElement) => {
                let rect = item.getBoundingClientRect();
                if (rect.left < x && rect.left + rect.width > x && rect.top < y && rect.top + rect.height > y) {
                    return true;
                }
            };
            const checkContain = (dom: HTMLElement) => {
                if (dom.tagName == "svg") {
                    return checkInside(dom);
                } else if (dom.children.length > 0) {
                    let flag = false;
                    for (let item of dom.children) {
                        flag = checkContain(item as HTMLElement);
                        if (flag) {
                            return true;
                        }
                    }
                    return flag;
                } else if (dom.getAttributeNames().includes("type")) {
                    return checkInside(dom);
                }
            };
            if (svg) {
                let child = svg.children;
                let needHidden = false;
                for (let item of child) {
                    if (needHidden) {
                        continue;
                    }
                    if (item.id == "slide") {
                        needHidden = checkInside(item as HTMLElement);
                    } else if (item.id == "selectGroup") {
                        needHidden = checkContain(item as HTMLElement);
                    }
                }
                if (needHidden) {
                    window["_view"].forceCleanEchart();
                }
            }
        }

        get shapeId() {
            return this.$store.state.shape.id;
        }
        get activeChartId() {
            return this.$store.state.newEchart.activeChartId;
        }
        get activeChartType() {
            return this.$store.state.newEchart.activeChartType;
        }
        updateStoreOption(chartObj) {
            // if (this.shapeId == this.chartInfo.itemId) {
            //     const option = this.chart.getOption();
            //     let str = JSON.stringify(option);
            //     this.$store.commit("setNewEChartState", {
            //         key: "activeChartOption",
            //         value: str,
            //     });
            // }
            // this.$bus.$emit("updateEchartData"); 移至Echart根组件watch监听
            this.debounceInsert(chartObj);
        }
        applyOption(data) {
            if (this.activeChartId != this.chartInfo.itemId) return;
            const chartType = this.activeChartType;
            const chartId = this.chartInfo.itemId;
            const cover = data.force;
            const originOption = this.chart.getOption();
            const cleanOption = () => {
                let keys = Object.keys(data);
                for (let key of keys) {
                    delete originOption[key];
                }
            };
            const onFinish = () => {
                this.updateStoreOption({ chartType, chartId });
                this.chart.off("finished", onFinish);
            };
            this.chart.on("finished", onFinish);

            if (data.force) {
                delete data.force;
                cleanOption();
            }
            this.chart.setOption(fixSetOption(Object.assign(originOption, data)), cover);
        }

        debounceInsert = debounce(this.insertToPage, 60);
        applyOptionWhenThemeChange() {
            // if (this.activeChartId != this.chartInfo.itemId) return;
            const chartType = this.activeChartType;
            const chartId = this.chartInfo.itemId;
            const originOption = this.chart.getOption();
            formatEchartOptions(originOption, window["_view"]._isDarkTheme(), chartType);

            const onFinish = () => {
                this.updateStoreOption({ chartType, chartId });
                this.chart.off("finished", onFinish);
            };
            this.chart.on("finished", onFinish);

            this.chart.setOption(fixSetOption(Object.assign(originOption)));
        }

        renderSvg(option) {
            return new Promise((rs) => {
                const onFinish = () => {
                    this.svgChart.off("finished", onFinish);
                    rs(this.svgChart.renderToSVGString());
                };
                this.svgChart.on("finished", onFinish);
                this.svgChart.setOption(fixSetOption(option));
            });
        }
        async insertToPage(obj) {
            const { chartType, chartId } = obj;
            const option = this.chart.getOption();
            const svgPreview = await this.renderSvg(option);

            let params = JSON.stringify([svgPreview, JSON.stringify(option), chartType, chartId]);
            window["_view"].insertEchart(params, true);
        }
        showEditor() {
            clearTimeout(this.clickTimer);
            let delay = 0;
            if (!this.$store.state.ui._activeShape) {
                delay = 300;
                window["_view"].selectEchartBox(this.chartInfo.itemId);
            }
            setTimeout(() => {
                this.$store.commit("setStatus", {
                    type: "isEChartData",
                    data: true,
                });
                this.$bus.$emit("itemChosen", "echart");
                this.sensors.track("editEchart", {
                    Catalogues: "shortcutToolbar",
                    content: "editDate",
                });
            }, delay);
        }
        handleLeave(e) {
            if (e.toElement?.tagName != "svg") {
                window["_view"].cleanEchartbox();
            }
        }

        created() {}
        mounted() {
            EchartTheme.registerEchartTheme(echarts);
            this.$bus.$on("setEditEchartData", this.applyOption);
            this.$bus.$on("setEditEchartDataAdaptorThemeChange", this.applyOptionWhenThemeChange);
            this.chartInfo && this.initChart(this.chartInfo.option);
            this.$bus.$on("changeEchartTheme", this.useTheme);
        }
        beforeDestroy() {
            this.$bus.$off("setEditEchartData", this.applyOption);
            this.$bus.$off("changeEchartTheme", this.useTheme);
            this.$bus.$off("setEditEchartDataAdaptorThemeChange", this.applyOptionWhenThemeChange);
        }
    }
</script>
<i18n>
{
    "zh": {},
    "en": {},
    "fr": {},
    "de": {},
    "jp": {},
    "es": {},
    "pt": {},
    "it": {},
    "tw": {},
    "kr": {}
}
</i18n>
